import { useEffect, useState, useCallback, useRef } from "react";
import { api, apiError, API } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "../../components/ui/sheet";
import { Plus, PencilSimple, Trash, UserCircle, UploadSimple, Phone, EnvelopeSimple, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";

const IMG_BASE = API.replace(/\/api$/, "");
const EMPTY = { name: "", phone: "", email: "", pincode: "" };

export default function PartnerEngineers() {
  const [rows, setRows] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const photoRef = useRef(null);

  const load = useCallback(() => {
    api.get("/partner/engineers").then((r) => setRows(r.data));
    api.get("/partner/pincodes").then((r) => setPincodes(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (e) => { setForm({ name: e.name, phone: e.phone, email: e.email, pincode: e.pincode }); setEditId(e.id); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.phone) { toast.error("Name and mobile number are required"); return; }
    if (!form.pincode) { toast.error("Please select a pincode"); return; }
    try {
      if (editId) await api.put(`/partner/engineers/${editId}`, form);
      else await api.post("/partner/engineers", form);
      toast.success(`Engineer ${editId ? "updated" : "added"}`);
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (e) => {
    if (!window.confirm(`Remove engineer ${e.name}?`)) return;
    await api.delete(`/partner/engineers/${e.id}`); toast.success("Removed"); load();
  };
  const uploadPhoto = async (eng, file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/partner/engineers/${eng.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated"); load();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };
  const geo = pincodes.find((p) => p.pincode === form.pincode);

  return (
    <>
      <PageHeader title="My Engineers" subtitle="Add your field engineers and assign them jobs"
        actions={<Button onClick={openNew} className="rounded-sm gap-2" data-testid="add-engineer-btn"><Plus size={16} weight="bold" /> Add Engineer</Button>} />
      <div className="p-8">
        {pincodes.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-sm text-amber-800 mb-6">
            You don't have any assigned service pincodes yet. Contact the Fixigo team to add your coverage areas before adding engineers.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="engineers-grid">
          {rows.length === 0 && <p className="text-muted-foreground">No engineers added yet.</p>}
          {rows.map((e) => (
            <div key={e.id} className="border border-border rounded-lg bg-card p-5" data-testid={`engineer-card-${e.id}`}>
              <div className="flex items-start gap-3">
                {e.photo_url
                  ? <img src={`${IMG_BASE}${e.photo_url}`} alt={e.name} className="w-14 h-14 rounded-full object-cover border border-border" />
                  : <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center"><UserCircle size={32} className="text-muted-foreground/50" /></div>}
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold truncate">{e.name}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><Phone size={12} /> {e.phone}</div>
                  {e.email && <div className="text-xs text-muted-foreground flex items-center gap-1 truncate"><EnvelopeSimple size={12} /> {e.email}</div>}
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1">
                <MapPin size={13} weight="fill" className="text-primary" /> {e.pincode} · {e.city}{e.state ? `, ${e.state}` : ""}
              </div>
              <div className="flex gap-2 mt-4">
                <label className="flex-1">
                  <input type="file" accept="image/*" className="hidden" onChange={(ev) => { uploadPhoto(e, ev.target.files?.[0]); ev.target.value = ""; }} data-testid={`eng-photo-${e.id}`} />
                  <span className="flex items-center justify-center gap-1 text-xs border border-border rounded-sm py-1.5 cursor-pointer hover:bg-secondary"><UploadSimple size={13} /> Photo</span>
                </label>
                <Button variant="outline" size="sm" className="rounded-sm flex-1 gap-1" onClick={() => openEdit(e)} data-testid={`edit-eng-${e.id}`}><PencilSimple size={13} /> Edit</Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(e)} data-testid={`del-eng-${e.id}`}><Trash size={15} /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="engineer-form">
          <SheetHeader><SheetTitle className="font-heading">{editId ? "Edit Engineer" : "Add Engineer"}</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            <div><Label className="label-caps">Engineer Name</Label><Input data-testid="eng-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1.5" /></div>
            <div><Label className="label-caps">Mobile Number</Label><Input data-testid="eng-field-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm mt-1.5" /></div>
            <div><Label className="label-caps">Email (optional)</Label><Input data-testid="eng-field-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm mt-1.5" /></div>
            <div>
              <Label className="label-caps">Pincode</Label>
              <Select value={form.pincode} onValueChange={(v) => setForm({ ...form, pincode: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="eng-field-pincode"><SelectValue placeholder="Select from your areas" /></SelectTrigger>
                <SelectContent>
                  {pincodes.map((p) => <SelectItem key={p.pincode} value={p.pincode}>{p.pincode} — {p.city || "?"}{p.state ? `, ${p.state}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              {geo && <p className="text-xs text-muted-foreground mt-1.5">City: <b>{geo.city || "—"}</b> · State: <b>{geo.state || "—"}</b></p>}
              <p className="text-xs text-muted-foreground mt-1">Only your assigned service pincodes are available.</p>
            </div>
            {editId && <p className="text-xs text-muted-foreground">Upload the engineer photo from the card after saving.</p>}
          </div>
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={save} data-testid="eng-save">Save</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
